import { useState } from 'react'
import type { DockviewApi } from 'dockview'
import { TelephonyProvider, useConnectionStatus, useStation } from './state/TelephonyProvider'
import { useCommand } from './state/useCommand'
import { useDial } from './state/useDial'
import { useLastDialed } from './state/dial'
import { useKeyboardShortcuts } from './state/useKeyboardShortcuts'
import { Workspace, openPanel, resetLayout } from './workspace/Workspace'
import { PANELS } from './workspace/panels'
import { SKINS, useSkin, setSkin } from './state/skin'
import { isLive, hasToken, goToLogin } from './state/auth'
import { ToastProvider } from './ui/Toast'
import { TelephonyCommandNames } from './contract'
import { Dialpad } from './panels/Dialpad'
import { CallForwardDialog } from './panels/CallForwardDialog'
import { ScreenPop } from './panels/ScreenPop'
import {
  IconHeadset, IconRefresh, IconBell, IconBellOff, IconArrowForward, IconKeyboard, IconPhone,
  IconWifiOff, IconAlertTriangle
} from './ui/icons'

function StatusPill() {
  const status = useConnectionStatus()
  const cls =
    status === 'connected' ? 'sb-pill sb-pill-ok'
      : status === 'disconnected' ? 'sb-pill sb-pill-off'
        : 'sb-pill sb-pill-warn'
  return <span className={cls}>{status}</span>
}

function ModeChip() {
  if (!isLive()) return <span className="sb-pill sb-pill-off" title="running on the mock source">mock</span>
  if (!hasToken()) {
    return <button className="sb-tool-btn" onClick={goToLogin} title="sign in to the live bridge">sign in</button>
  }
  return <span className="sb-pill sb-pill-ok" title="wired to the cloud bridge">live</span>
}

function DndButton() {
  const run = useCommand()
  const station = useStation()
  const [on, setOn] = useState(false)
  const toggle = async () => {
    const next = !on
    const res = await run(
      { command: TelephonyCommandNames.SetDnd, params: { extension: station.extension, enabled: next } },
      { successMessage: `Do Not Disturb ${next ? 'on' : 'off'}` }
    )
    if (res && res.status === 'success') setOn(next)
  }
  return (
    <button className={on ? 'sb-tool-btn on' : 'sb-tool-btn'} onClick={() => void toggle()}
      title="Toggle Do Not Disturb for your station" aria-pressed={on}>
      {on ? <IconBellOff size={14} stroke={1.75} /> : <IconBell size={14} stroke={1.75} />}
      {on ? 'DND on' : 'DND'}
    </button>
  )
}

function ReconnectBanner() {
  const status = useConnectionStatus()
  if (status === 'reconnecting') {
    return <div className="sb-reconnect"><IconAlertTriangle size={14} stroke={1.9} /> Reconnecting to the telephony bridge…</div>
  }
  if (status === 'disconnected') {
    return <div className="sb-reconnect off"><IconWifiOff size={14} stroke={1.9} /> Connection lost — telephony updates are paused.</div>
  }
  return null
}

function Topbar({ api }: { api: DockviewApi | null }) {
  const station = useStation()
  const skin = useSkin()
  const [cfOpen, setCfOpen] = useState(false)
  return (
    <div className="sb-topbar">
      <div className="sb-brand">
        <span className="ico"><IconHeadset size={18} stroke={1.75} /></span>
        <span>StationBridge</span>
        <span className="muted" style={{ fontWeight: 400 }}>ext {station.extension}</span>
      </div>
      <div className="sb-toolbar">
        <select className="sb-select" value="" title="Open / focus a panel"
          onChange={(e) => { if (api && e.target.value) openPanel(api, e.target.value); e.target.value = '' }}>
          <option value="">panels…</option>
          {PANELS.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        <button className="sb-tool-btn" onClick={() => api && resetLayout(api)}>
          <IconRefresh size={14} stroke={1.75} /> reset
        </button>
        <DndButton />
        <button className="sb-tool-btn" onClick={() => setCfOpen(true)} title="Call forwarding">
          <IconArrowForward size={14} stroke={1.75} /> forward
        </button>
        <select className="sb-select" value={skin} title="Skin" onChange={(e) => setSkin(e.target.value)}>
          {SKINS.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <ModeChip />
        <StatusPill />
      </div>
      {cfOpen ? <CallForwardDialog onClose={() => setCfOpen(false)} /> : null}
    </div>
  )
}

function Footer() {
  const dial = useDial()
  const run = useCommand()
  const station = useStation()
  const last = useLastDialed()
  const [num, setNum] = useState('')
  const [padOpen, setPadOpen] = useState(false)

  const call = () => { if (num.trim()) { dial(num); setNum('') } }
  const redial = () => {
    if (!last) return
    void run(
      { command: TelephonyCommandNames.Redial, params: { extension: station.extension, destination: last, auto_answer: true } },
      { successMessage: `Redialing ${last}` }
    )
  }
  return (
    <div className="sb-footer">
      <input className="sb-input" inputMode="tel" placeholder="Number or extension"
        value={num} onChange={(e) => setNum(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') call() }} aria-label="Dial a number" />
      <button className="sb-btn-primary" onClick={call} disabled={!num.trim()}>
        <IconPhone size={15} stroke={1.75} /> Call
      </button>
      <button className="sb-tool-btn" onClick={redial} disabled={!last}
        title={last ? `Redial ${last}` : 'No number to redial yet'}>
        <IconRefresh size={14} stroke={1.75} /> Redial
      </button>
      <button className="sb-tool-btn" onClick={() => setPadOpen(true)} title="Open dial pad">
        <IconKeyboard size={14} stroke={1.75} /> Keypad
      </button>
      <span className="sb-footer-spacer" />
      <span className="hint">Last dialed: {last || '—'}</span>
      {padOpen ? <Dialpad onClose={() => setPadOpen(false)} /> : null}
    </div>
  )
}

function AuthGate() {
  return (
    <div className="sb-gate">
      <div className="sb-gate-card">
        <span className="ico"><IconHeadset size={34} stroke={1.5} /></span>
        <h1>Sign in to StationBridge</h1>
        <p>This console is wired to the live telephony bridge and needs you to sign in with your
          Magnify suite account before it can show your calls.</p>
        <button className="sb-btn-primary" onClick={goToLogin}>Sign in</button>
      </div>
    </div>
  )
}

function AppShell() {
  const [api, setApi] = useState<DockviewApi | null>(null)
  useKeyboardShortcuts()
  // Live bridge but no token -> full-screen sign-in gate (mock mode never gates).
  if (isLive() && !hasToken()) {
    return (
      <div className="sb-app">
        <AuthGate />
      </div>
    )
  }
  return (
    <div className="sb-app">
      <Topbar api={api} />
      <ReconnectBanner />
      <Workspace onApi={setApi} />
      <Footer />
      <ScreenPop />
    </div>
  )
}

export default function App() {
  return (
    <ToastProvider>
      <TelephonyProvider>
        <AppShell />
      </TelephonyProvider>
    </ToastProvider>
  )
}
